import { PageController } from '@client/components/_core/layout/PageController';
import { message } from '@client/components/_ui/feedback/message';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useOrgId } from '@client/hooks/business/org/useOrgId';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { match } from 'ts-pattern';
import { type ServiceProvider, ServiceProviderForm } from './ServiceProviderForm';

export const Route = createFileRoute('/console/$orgId/mspr/service-providers/search/add')({
    staticData: {
        presentation: 'overlay',
    },
    component: RouteComponent,
});

function RouteComponent() {
    return (
        <PageController route={Route}>
            <AddServiceProvider />
        </PageController>
    );
}

function AddServiceProvider() {
    const { trpc, trpcUtils } = useTrpc();
    const [open, setOpen] = useState(true);
    const orgId = useOrgId();
    const navigate = useNavigate();
    const { mutate: createProvider, isPending: isCreating } = trpc.mspr.msp.createProvider.useMutation();

    const handleSubmit = (values: ServiceProvider) => {
        message.loading('Creating service provider...');

        createProvider(
            {
                businessName: values.businessName!,
                abn: values.abn!,
                lei: values.lei ?? null,
                contactPerson: values.contactPerson!,
                contactEmail: values.contactEmail || null,
                contactPhone: values.contactPhone || null,
                providerType: values.providerType!,
                supportedThirdPartyId: values.supportedThirdPartyId || null,
                handlingPartyIds: values.handlingPartyIds!,
                ownerId: values.ownerId!,
                rating: values.rating || null,
                status: values.status || null,
            },
            {
                onSuccess: () => {
                    message.success('Service provider created');
                    trpcUtils.mspr.msp.listProviders.invalidate();
                    setOpen(false);
                },
            },
        );
    };

    return (
        <ServiceProviderForm
            open={open}
            state={match({ isCreating })
                .with({ isCreating: true }, () => 'submitting' as const)
                .otherwise(() => 'ready' as const)}
            onSubmit={handleSubmit}
            onCloseFinished={() => {
                navigate({ to: '/console/$orgId/mspr/service-providers/search', params: { orgId } });
            }}
        />
    );
}
